import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, X } from 'lucide-react';
import { validatePassword } from '../../utils/passwordUtils';

interface CreatePasswordModalProps {
  isOpen: boolean;  
  onClose: () => void;
  onSubmit: (_password: string, _confirmPassword: string) => void;
  isLoading: boolean;
}

const CreatePasswordModal: React.FC<CreatePasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setIsValid(false);
      return;
    } else {
      setIsValid(true);
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    } else {
      setPasswordError(null);
    }
    onSubmit(newPassword, confirmPassword);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Desktop Create Password Modal */}
     {window.innerWidth > 768 ? (
        <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 md:flex hidden items-center justify-center z-50 zoomOutLevel">
          <div className="bg-[var(--modal-background)] rounded-2xl p-10 max-w-lg w-full mx-4 relative">
            {/* Header */}
            <div className="text-center mb-4 px-4">
              <h4 className="sm:text-3xl text-2xl font-light text-[var(--color-text)] mb-2">Create New Password</h4>
              <p className="text-[var(--color-sub-title-text)] sm:text-sm text-xs text-center sm:px-6">
                Enter and confirm your new password to complete the reset process
              </p>
              <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors absolute right-7 top-5"
                >
                  <X className="h-6 w-6" />
                </button>
            </div>

            {/* Password Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-base font-normal text-[var(--color-text)] mb-2">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`} alt="Lock" />
                  </div>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setIsValid(true);
                    }}
                    className={`w-full pl-10 pr-12 py-3 border dark:border-0 dark:focus:border rounded-lg focus:border-[#22c55e] dark:bg-[#F5F5F533] bg-[#F5F5F5] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none ${!isValid && 'border-[#E67E7D]'}`}
                    placeholder="Enter New Password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-base font-normal text-[var(--color-text)] mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`} alt="Lock" />
                  </div>
                  <input  
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    className={`w-full pl-10 pr-12 py-3 border dark:border-0 dark:focus:border rounded-lg focus:border-[#22c55e] dark:bg-[#F5F5F533] bg-[#F5F5F5] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none ${passwordError && 'border-[#E67E7D]'}`}
                    placeholder="Re-enter New Password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                      </svg>
                    )}
                  </button>
                </div>
                {passwordError && (
                  <div className="flex items-center mt-2"> 
                    <Info className="h-5 w-5 text-[#E67E7D] mr-2" />
                    <p className="text-[#E67E7D] text-sm">{passwordError}</p>
                  </div>
                )}
              </div>

              {/* Password Requirements */}
              <div className={`border-2 border-dashed ${isValid ? 'border-[var(--color-sub-title-text)]' : 'border-[#E67E7D]'} rounded-lg p-4 mt-4`}>
                <div className="flex items-center">
                  <Info className={`h-5 w-5 ${isValid ? 'text-[var(--color-sub-title-text)]' : 'text-[#E67E7D]'} mt-0.5 mr-3 flex-shrink-0`} />
                  <p className={`text-[var(--color-sub-title-text)] text-sm ${!isValid && 'text-[#E67E7D]'}`}>
                    Password must be at least 8 characters and include uppercase, lowercase, number and special character.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                className="px-10 signin-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center mt-6">
              <p className="text-[var(--color-sub-title-text)]">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-[#217C70] hover:text-[#217c708c] font-bold underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="login-container overflow-y-auto flex-col gap-5">
        {/* Mobile, tablet Create Password Modal */}
          {/* Header */}
          <div className="text-center sm:mx-12 mx-6 z-10">
            <img src={`${process.env.PUBLIC_URL}/SmallScreenImages/CreatePassword.svg`} alt="Create Password Image" className="w-16 h-16 mx-auto mb-4 object-cover" />
            <h4 className="sm:text-4xl text-[1.5rem] font-light text-white mb-4">Create New Password</h4>
            <p className="text-white sm:text-base font-normal text-sm text-center">
              Enter and confirm your new password to complete the reset process
            </p>
            <button
                onClick={onClose}
                className="text-white transition-colors fixed left-7 top-5"
              >
                <ArrowLeft className="h-6 w-6" />
            </button>
          </div>
          <div className="bg-[var(--modal-background)] rounded-[1.25rem] p-6 w-full relative">

            {/* Password Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-base font-normal text-[var(--color-text)] mb-2">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`} alt="Lock" />
                  </div>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setIsValid(true);
                    }}
                    className={`w-full pl-10 pr-12 py-3 border dark:border-0 dark:focus:border rounded-lg focus:border-[#22c55e] bg-[#FAFAFA] dark:bg-[#FAFAFA33] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none ${!isValid && 'border-[#E67E7D]'}`}
                    placeholder="Enter New Password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-base font-normal text-[var(--color-text)] mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`} alt="Lock" />
                  </div>
                  <input  
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    className={`w-full pl-10 pr-12 py-3 border dark:border-0 dark:focus:border rounded-lg focus:border-[#22c55e] bg-[#FAFAFA] dark:bg-[#FAFAFA33] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none ${passwordError && 'border-[#E67E7D]'}`}
                    placeholder="Re-enter New Password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                      </svg>
                    ) : (
                      <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                      </svg>
                    )}
                  </button>
                </div>
                {passwordError && (
                  <div className="flex items-center mt-2"> 
                    <Info className="h-5 w-5 text-[#E67E7D] mr-2" />
                    <p className="text-[#E67E7D] text-sm">{passwordError}</p>
                  </div>
                )}
              </div>

              {/* Password Requirements */}
              <div className={`border-2 border-dashed ${isValid ? 'border-[var(--color-sub-title-text)]' : 'border-[#E67E7D]'} rounded-lg p-4 mt-4`}>
                <div className="flex items-center">
                  <Info className={`h-5 w-5 ${isValid ? 'text-[var(--color-sub-title-text)]' : 'text-[#E67E7D]'} mt-0.5 mr-3 flex-shrink-0`} />
                  <p className={`text-[var(--color-sub-title-text)] text-sm ${!isValid && 'text-[#E67E7D]'}`}>
                    Password must be at least 8 characters and include uppercase, lowercase, number and special character.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                className="px-10 signin-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center mt-6">
              <p className="text-[var(--color-sub-title-text)]">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-[#217C70] hover:text-[#217c708c] font-bold underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePasswordModal; 